import { useEffect, useState } from "react";
import { DesktopTimePicker, DigitalClock } from "@mui/x-date-pickers";
import type { DesktopTimePickerProps, TimeView } from "@mui/x-date-pickers";
import { forwardRef } from "react";

export type StartTimePickerProps = DesktopTimePickerProps<Date> & {
  name: string;
  formatString?: string;
  setStartDateTime?: any;
  selectedEvent?: any;
  dateTime?: any;
  setValue?: any;
  minTime?: Date | null;
  maxTime?: Date | null;
  onChange?: any;
  pickupWindow?: number;
};

export default function StartTimePicker({
  name,
  setStartDateTime,
  selectedEvent,
  dateTime,
  setValue,
  minTime,
  maxTime,
  onChange,
  pickupWindow = 0,
  ...inpuProps
}: StartTimePickerProps) {
  const [selectedTime, setSelectedTime] = useState<Date>(dateTime);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (dateTime) {
      setSelectedTime(dateTime);
    }
  }, [dateTime, minTime]);

  const getEndTime = (startTime: Date | null): Date | null => {
    if (!startTime || !pickupWindow) return null;
    const end = new Date(startTime);
    end.setMinutes(end.getMinutes() + pickupWindow);
    return end;
  };

  const formatTime = (date: Date | null): string => {
    if (!date) return "";
    return date.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  const getValidHours = () => {
    if (!minTime || !maxTime) return [];
    const hours: number[] = [];
    const minHour = minTime.getHours();
    const maxHour = maxTime.getHours();

    if (maxTime < minTime) {
      for (let i = minHour; i < 24; i++) hours.push(i);
      for (let i = 0; i <= maxHour; i++) hours.push(i);
    } else {
      for (let i = minHour; i <= maxHour; i++) hours.push(i);
    }
    return hours;
  };

  const validHours = getValidHours();
  const hasAM = validHours.some((h) => h < 12);
  const hasPM = validHours.some((h) => h >= 12);

  const shouldDisableTime = (value: Date, view: TimeView) => {
    if (!minTime || !maxTime) return false;

    const hour = value.getHours();
    const minute = value.getMinutes();
    const minHour = minTime.getHours();
    const maxHour = maxTime.getHours();
    const minMinute = minTime.getMinutes();
    const maxMinute = maxTime.getMinutes();

    if (view === "hours") {
      return !validHours.includes(hour);
    }

    if (view === "minutes") {
      if (hour === minHour && minute < minMinute) return true;
      if (hour === maxHour && minute > maxMinute) return true;
      return false;
    }

    return false;
  };

  const PickupWindowLayout = forwardRef<HTMLDivElement, any>((props, ref) => {
    const generateSlots = (): Date[] => {
      if (!minTime || !maxTime || !pickupWindow) return [];
      const slots: Date[] = [];
      const cursor = new Date(minTime);
      cursor.setSeconds(0, 0);

      const end = new Date(maxTime);
      end.setSeconds(0, 0);

      while (cursor <= end) {
        slots.push(new Date(cursor));
        cursor.setMinutes(cursor.getMinutes() + pickupWindow);
      }
      return slots;
    };

    const slots = generateSlots();

    return (
      <div ref={ref} style={{ padding: "8px", minWidth: "260px" }}>
        <div
          style={{
            maxHeight: "300px",
            overflowY: "auto",
          }}
        >
          {slots.map((slot, idx) => {
            const endSlot = getEndTime(slot);
            const isSelected =
              selectedTime &&
              slot.getHours() === selectedTime.getHours() &&
              slot.getMinutes() === selectedTime.getMinutes();

            return (
              <div
                key={idx}
                onClick={() => {
                  if (props.onChange) {
                    props.onChange(slot);
                    setSelectedTime(slot);
                    setOpen(false);
                  }
                }}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  padding: "10px 14px",
                  fontSize: "20px",
                  cursor: "pointer",
                  borderRadius: "4px",
                  backgroundColor: isSelected
                    ? "rgb(246, 246, 246)"
                    : "transparent",                 
                }}
                onMouseEnter={(e) => {
                  if (!isSelected)
                    (e.currentTarget as HTMLDivElement).style.backgroundColor =
                      "rgba(0,0,0,0.04)";
                }}
                onMouseLeave={(e) => {
                  if (!isSelected)
                    (e.currentTarget as HTMLDivElement).style.backgroundColor =
                      "transparent";
                }}
              >
                <span>{formatTime(slot)}</span>
                <span style={{ color: "#888", fontSize: "18px"}}> ― </span>
                <span>{formatTime(endSlot)}</span>
              </div>
            );
          })}
        </div>
      </div>
    );
  });

  PickupWindowLayout.displayName = "PickupWindowLayout";

  const StandardLayout = forwardRef<HTMLDivElement, any>((props, ref) => {
    return (
      <div ref={ref}>
        <div style={{ padding: "8px" }}>
          <DigitalClock
            value={props.value}
            onChange={(newValue: Date | null) => {
              if (props.onChange) {
                props.onChange(newValue);
                setOpen(false);
              }
            }}
            minTime={minTime}
            maxTime={maxTime}
            shouldDisableTime={shouldDisableTime}
            skipDisabled
            timeStep={15}
            sx={{
              maxHeight: "300px",
              "& .MuiList-root": {
                maxHeight: "300px",
              },
              "& .MuiButtonBase-root": {
                fontSize: "20px",
                padding: "10px 14px",
              },
            }}
          />
        </div>
      </div>
    );
  });

  StandardLayout.displayName = "StandardLayout";

  const getDisplayValue = (): Date | null => {
    if (!pickupWindow || !selectedTime) return selectedTime ?? null;
    return selectedTime;
  };

  const getTextFieldValue = (): string => {
    if (!pickupWindow || !selectedTime) return "";
    const end = getEndTime(selectedTime);
    return `${formatTime(selectedTime)}   ―   ${formatTime(end)}`;
  };

  return (
    <DesktopTimePicker
      {...inpuProps}
      value={getDisplayValue()}
      minTime={minTime}
      maxTime={maxTime}
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      onChange={(val, ctx) => {
        if (onChange) onChange(val, ctx);
        if (val) setSelectedTime(val);
      }}
      slots={{
        layout: pickupWindow ? PickupWindowLayout : StandardLayout,
      }}
      slotProps={{
        textField: {
          onKeyDown: (e) => {
            e.preventDefault();
          },
          inputProps: pickupWindow
            ? {
                value: getTextFieldValue(),
                readOnly: true,
              }
            : undefined,
          error: false,
          sx: {
            "& .MuiOutlinedInput-root": {
              "& fieldset": {
                borderColor: "rgba(0, 0, 0, 0.23) !important",
              },
              "&:hover fieldset": {
                borderColor: "rgba(0, 0, 0, 0.87) !important",
              },
              "&.Mui-error fieldset": {
                borderColor: "rgba(0, 0, 0, 0.23) !important",
              },
            },
            "& .MuiFormHelperText-root": {
              display: "none !important",
            },
            "& .Mui-error": {
              color: "inherit !important",
            },
          },
        },
        popper: {
          className: !hasPM ? "hide-pm" : !hasAM ? "hide-am" : "",
        },
      }}
      data-testid="controlled-time-picker"
    />
  );
}